import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import serialize from '../store/mixins/serialize'

const runManualAction = async ({ ledgerIds, action, payload }) => {
  const url = Silverfin.Routes.insight_action_runs_path(Silverfin.currentFirmId)

  try {
    const data = await SFetch.json(url, {
      method: 'POST',
      body: {
        insight_action_run: {
          ledger_ids: ledgerIds,
          action: action,
          payload: payload,
        },
      },
    })

    return data
  } catch (e) {
    SfLog.info('Failed to run a manual trigger action', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    throw new Error(e) // throw to communicate this back to the user in the actions modal
  }
}

const fetchNonActionableCompanies = async ({
  actionName,
  companyIds,
  payload,
}) => {
  const url =
    Silverfin.Routes.non_actionable_companies_insight_action_runs_path(
      Silverfin.currentFirmId
    )

  try {
    const data = await SFetch.json(url, {
      method: 'POST',
      body: {
        actionName,
        companyIds,
        ...payload,
      },
    })

    return data
  } catch (e) {
    SfLog.info('[Insight triggers] Failed to fetch non actionable Companies', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

const fetchFilterDropdownValues = async () => {
  const firmId = Silverfin.currentFirmId
  const filterDropdownValuesEndpoint =
    Silverfin.Routes.filter_dropdown_values_companies_overview_index_path(
      firmId
    )

  try {
    const data = await SFetch.json(filterDropdownValuesEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(
      `Cannot load filter dropdown values for Firm ${Silverfin.currentFirmId}`
    )

    return {
      company_templates: [],
      currencies: [],
      countries: [],
      company_types: [],
      sync_entities: [],
      report_frequencies: [],
      firm_workflows: [],
      workflow_statuses: [],
      account_mapping_lists: [],
    }
  }
}

const fetchReconciliationTexts = async () => {
  const url = Silverfin.Routes.reconciliation_texts_path(
    Silverfin.currentFirmId
  )

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch (e) {
    SfLog.info('Failed to load insights reconciliation texts', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

const fetchSegment = async ({ segmentId, partnerSegmentId }) => {
  const firmId = Silverfin.currentFirmId
  const segmentEndpoint = Silverfin.Routes.segment_path(firmId, segmentId)

  const url = `${segmentEndpoint}${
    partnerSegmentId
      ? '?' +
        new URLSearchParams({
          partner_segment_id: partnerSegmentId,
        })
      : ''
  }`

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(`Cannot load Segment with ID ${segmentId} for Firm ${firmId}`)

    return {}
  }
}

const fetchSegments = async () => {
  const firmId = Silverfin.currentFirmId
  const segmentsEndpoint = Silverfin.Routes.segments_path(firmId)

  try {
    const data = await SFetch.json(segmentsEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch (e) {
    SfLog.info('Failed to load insights segments', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

const fetchTriggers = async (segmentId) => {
  const url = Silverfin.Routes.insight_triggers_path({
    firm_id: Silverfin.currentFirmId,
    segment_id: segmentId,
  })

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(
      `Cannot load Triggers with Segment ID ${segmentId} for Firm ${Silverfin.currentFirmId}`
    )

    return {}
  }
}

const fetchUsers = async () => {
  const firmId = Silverfin.currentFirmId
  const usersEndpoint =
    Silverfin.Routes.users_companies_overview_index_path(firmId)

  try {
    const data = await SFetch.json(usersEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(`Cannot load Users for Firm ${Silverfin.currentFirmId}`)

    return {
      users: [],
    }
  }
}

const fetchSubmissionDropdownData = async () => {
  const firmId = Silverfin.currentFirmId
  const url =
    Silverfin.Routes.submissions_dropdown_values_companies_overview_index_path(
      firmId
    )

  try {
    const data = await SFetch.json(url, undefined, { camelizeJSON: false })
    return data
  } catch {
    SfLog.warn(`Cannot load Submission Dropdown Data for Firm ${firmId}`)

    return { submissionData: [] }
  }
}

export default {
  fetchFilteredCompanies({
    rootState,
    rootGetters,
    requestRef,
    pendingRequestRef,
    noCache,
  }) {
    const firmId = Silverfin.currentFirmId
    const url = Silverfin.Routes.filter_companies_overview_index_path(firmId)

    const segment = rootState.segments.segment
    const segmentId = !rootGetters.isSegmentModified && segment?.id
    const partnerSegmentId =
      !rootGetters.isSegmentModified && segment?.partner_segment_id

    return SFetch.jsonWithAlert(url, {
      method: 'POST',
      body: {
        ledger_select: serialize.ledgerSelectSettings(rootState),
        filters: serialize.filterSettings(
          rootState.filters.activeFilters,
          rootState.filters.allFiltersByType
        ),
        request_ref: requestRef,
        pending_request_ref: pendingRequestRef,
        segment_id: segmentId,
        partner_segment_id: partnerSegmentId,
        no_cache: noCache,
      },
    })
  },
  fetchFilteredCompaniesRetry({
    rootState,
    requestRef,
    pendingRequestRef,
    missingCompanyIds,
  }) {
    const firmId = Silverfin.currentFirmId
    const url =
      Silverfin.Routes.filter_retry_companies_overview_index_path(firmId)

    return SFetch.jsonWithAlert(
      url,
      {
        method: 'POST',
        body: {
          ledger_select: serialize.ledgerSelectSettings(rootState),
          filters: serialize.filterSettings(
            rootState.filters.activeFilters,
            rootState.filters.allFiltersByType
          ),
          request_ref: requestRef,
          pending_request_ref: pendingRequestRef,
          missing_company_ids: missingCompanyIds,
        },
      },
      {
        camelizeJSON: false,
      }
    )
  },
  fetchFilterDropdownValues,
  fetchReconciliationTexts,
  fetchSegment,
  fetchSegments,
  fetchTriggers,
  fetchUsers,
  fetchSubmissionDropdownData,
  runManualAction,
  fetchNonActionableCompanies,
  // eslint-disable-next-line camelcase
  cancelPendingRequest({ pending_request_ref }) {
    const firmId = Silverfin.currentFirmId
    const url =
      Silverfin.Routes.cancel_pending_request_companies_overview_index_path(
        firmId
      )

    return SFetch.jsonWithAlert(url, {
      method: 'POST',
      body: { pending_request_ref: pending_request_ref },
    })
  },
}
